import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MergeTag, getAllMergeTags, getMergeTagGroups } from '../../models/merge-tag.model';

interface TagGroup {
  name: string;
  expanded: boolean;
  tags: MergeTag[];
}

@Component({
  selector: 'app-merge-tags-panel',
  templateUrl: './merge-tags-panel.component.html',
  styleUrls: ['./merge-tags-panel.component.scss'],
})
export class MergeTagsPanelComponent implements OnInit {

  @Input() placedTagKeys: string[] = [];
  @Input() previewMode = false;
  @Output() tagDropped  = new EventEmitter<MergeTag>();
  @Output() addFreeText = new EventEmitter<void>();
  @Output() addDivider  = new EventEmitter<void>();

  public searchText = '';
  public groups: TagGroup[] = [];
  public filteredGroups: TagGroup[] = [];

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.buildGroups();
  }

  private buildGroups(): void {
    const g    = getMergeTagGroups(this.translate);
    const tags = getAllMergeTags(this.translate);
    const groupOrder = [g.CUSTOMER, g.JOB, g.ITEM, g.BARCODES_MEDIA, g.DOCUMENTS];

    this.groups = groupOrder.map(groupName => ({
      name: groupName,
      expanded: true,
      tags: tags.filter(t => t.group === groupName),
    }));
    this.applyFilter();
  }

  applyFilter(): void {
    const q = this.searchText.toLowerCase().trim();
    if (!q) {
      this.filteredGroups = this.groups.map(g => ({ ...g, tags: [...g.tags] }));
      return;
    }
    this.filteredGroups = this.groups
      .map(g => ({ ...g, tags: g.tags.filter(t => t.label.toLowerCase().includes(q)), expanded: true }))
      .filter(g => g.tags.length > 0);
  }

  toggleGroup(group: TagGroup): void {
    group.expanded = !group.expanded;
  }

  isPlaced(tag: MergeTag): boolean {
    return this.placedTagKeys.includes(tag.key);
  }

  getElementTypeIcon(tag: MergeTag): string {
    const icons: Record<string, string> = {
      field:   'pi-font',
      barcode: 'pi-barcode',
      qrcode:  'pi-qrcode',
      image:   'pi-image',
      table:   'pi-table',
      divider: 'pi-minus',
      header:  'pi-align-center',
    };
    return icons[tag.elementType] || 'pi-font';
  }

  // Called when user clicks a tag (alternative to drag for accessibility)
  addTag(tag: MergeTag): void {
    if (!this.isPlaced(tag)) {
      this.tagDropped.emit(tag);
    }
  }

  /** Serialises the tag into the native drag payload so the canvas can read it on drop. */
  onDragStart(tag: MergeTag, event: DragEvent): void {
    if (this.isPlaced(tag)) { event.preventDefault(); return; }
    event.dataTransfer?.setData('application/merge-tag', JSON.stringify(tag));
    event.dataTransfer!.effectAllowed = 'copy';
  }
}
